/**
 * useStaffWorkspace — the "where am I working?" context for staff
 * users. Resolves the logged-in user's team membership(s) into:
 *
 *   - host info (so the navbar can render "Working at [Host Name]")
 *   - the staff member's warehouse scope (UUIDs they're assigned to;
 *     ``null`` means all of the host's warehouses)
 *   - the host's actual warehouse list (resolved via /search/by-host)
 *   - intersection of those two = the warehouses this staff member
 *     can actually operate on
 *   - a session-pinned ``selectedWarehouseId`` so every page they
 *     navigate to scopes its data fetches consistently
 *
 * Why a hook (not a context provider): the data load is light, the
 * call cache is per-tab, and a hook keeps wiring boilerplate-free
 * for pages that only need the warehouseId. Pages that don't need
 * it pay nothing.
 *
 * Mirrors the ``useAdminHostScope`` pattern we built for super-admin
 * cross-host scoping — the API surface is intentionally similar so
 * pages can swap one for the other where the role differs.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import api from '../../services/api';

const STORAGE_KEY = 'staff_workspace_warehouse_id';

/** True iff the current logged-in user is a warehouse staff member. */
export const useIsStaffViewer = () => {
  const role = useSelector((s) => s.auth.user?.role);
  const norm = String(role || '').toUpperCase();
  return norm === 'STAFF' || norm === 'WAREHOUSE_STAFF';
};

const readPinnedWarehouse = () => {
  try { return sessionStorage.getItem(STORAGE_KEY) || null; }
  catch { return null; }
};

const writePinnedWarehouse = (id) => {
  try {
    if (id) sessionStorage.setItem(STORAGE_KEY, id);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
};

export const useStaffWorkspace = () => {
  const isStaff = useIsStaffViewer();
  const [memberships, setMemberships] = useState([]);
  const [activeHostId, setActiveHostId] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // 1) Pull the staff's memberships once on mount. The endpoint is
  // /team-members/me (added in identity-service) — returns every host
  // this user belongs to plus their per-host warehouse_scope.
  useEffect(() => {
    if (!isStaff) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data } = await api.get('/team-members/me');
        const list = data?.memberships || [];
        if (cancelled) return;
        // Filter to ACTIVE rows so a suspended membership doesn't
        // appear in the host switcher. SUSPENDED / REMOVED stay out.
        const activeOnly = list.filter((m) => m.status === 'ACTIVE');
        setMemberships(activeOnly);
        // Default the active host to the first ACTIVE membership.
        // Most staff have exactly one — multi-host is the long tail.
        if (activeOnly.length > 0) setActiveHostId(activeOnly[0].host_id);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load workspace.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff]);

  const activeMembership = useMemo(
    () => memberships.find((m) => m.host_id === activeHostId) || null,
    [memberships, activeHostId],
  );

  // 2) Resolve the active host's warehouse list. We use the public
  // /search/by-host endpoint already used elsewhere; this returns
  // ACTIVE listings for the host. Cross-referenced with the staff's
  // ``warehouse_scope`` (if non-null) so the picker only shows
  // warehouses the staff is actually assigned to.
  useEffect(() => {
    if (!isStaff || !activeHostId) {
      setWarehouses([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/search/by-host/${activeHostId}`, {
          params: { page: 1, page_size: 100 },
        });
        const items = data?.items || data?.results || [];
        const seen = new Set();
        const allHostWarehouses = items
          .filter((l) => l.warehouse_id && !seen.has(l.warehouse_id) && seen.add(l.warehouse_id))
          .map((l) => ({
            id: l.warehouse_id,
            label: l.title || l.name || 'Untitled warehouse',
            city: l.city || '',
          }));

        // If the membership has an explicit warehouse_scope (non-null
        // array), filter to that subset. Null/undefined scope means
        // "every warehouse under this host" — show them all.
        const scope = activeMembership?.warehouse_scope;
        const scoped = (Array.isArray(scope) && scope.length > 0)
          ? allHostWarehouses.filter((w) => scope.includes(w.id))
          : allHostWarehouses;

        if (cancelled) return;
        setWarehouses(scoped);

        // Pick a warehouse: prefer the session pin if it's still in
        // scope; otherwise auto-pick the first. Staff don't usually
        // care which one is "default" — they care that the picker
        // doesn't reset every refresh.
        const pinned = readPinnedWarehouse();
        const pinnedStillValid = pinned && scoped.some((w) => w.id === pinned);
        const next = pinnedStillValid ? pinned : (scoped[0]?.id || '');
        setWarehouseId(next);
        writePinnedWarehouse(next || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load warehouses.');
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff, activeHostId, activeMembership]);

  const setActiveHost = (hostId) => {
    setActiveHostId(hostId);
    writePinnedWarehouse(null);  // reset warehouse pin when switching hosts
    setWarehouseId('');
  };

  const setSelectedWarehouse = (id) => {
    setWarehouseId(id || '');
    writePinnedWarehouse(id || null);
  };

  return {
    isStaff,
    loading,
    error,
    /** All ACTIVE memberships for this user (one per host). */
    memberships,
    /** The host the user is currently operating under. */
    activeHostId,
    activeMembership,
    activeHost: activeMembership?.host || null,
    /** Switch active host — only useful when memberships.length > 1. */
    setActiveHost,
    /** Warehouses this staff can operate on (host warehouses ∩ scope). */
    warehouses,
    /** Currently-pinned warehouse id; persists across pages via sessionStorage. */
    warehouseId,
    setSelectedWarehouse,
    /** True when staff can't pick anything yet (no host or no warehouses). */
    blocked: isStaff && (!activeHostId || warehouses.length === 0 || !warehouseId),
    /** True when ready to fetch warehouse-scoped data. */
    ready: !(isStaff && (!activeHostId || warehouses.length === 0 || !warehouseId)),
  };
};

export default useStaffWorkspace;
